import { zodResolver } from '@hookform/resolvers/zod';
import { CountryMultiSelectField } from '@repo/customer-registration-kit/common/components/country-multi-select/country-multi-select-field';
import { getRecommendedCountries } from '@repo/customer-registration-kit/organizations/activity/utils';
import { BusinessRelationshipsDropdown } from '@repo/customer-registration-kit/organizations/components/business-relationships-dropdown/business-relationships-dropdown';
import { TextField } from '@repo/design-system-kit/components/text-field/text-field';
import { ResponsiveCta } from '@repo/shared-kit/components/responsive-cta/responsive-cta';
import { type ReactNode, useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { BackButton } from 'register-partners-js/react/components/back-button/back-button';
import {
  type CompanyActivityOrganizationInput,
  snapshotCompanyActivityOrganization,
} from 'register-partners-js/utils/company-activity';
import styles from './company-activity-form.module.css';
import {
  type CompanyActivityFormInputValues,
  type CompanyActivityFormValues,
  createCompanyActivityFormSchema,
} from './validations';

export type { CompanyActivityFormValues };

interface CompanyActivityFormProps {
  organization: CompanyActivityOrganizationInput;
  onSubmit: (values: CompanyActivityFormValues) => void | Promise<void>;
  goBack: () => void;
  isSubmitting: boolean;
  isGoingBack?: boolean;
}

export function CompanyActivityForm({
  organization: organizationData,
  onSubmit,
  goBack,
  isSubmitting,
  isGoingBack = false,
}: CompanyActivityFormProps): ReactNode {
  const organization = useMemo(
    () => snapshotCompanyActivityOrganization(organizationData),
    [organizationData]
  );
  const { formatMessage } = useIntl();
  const recommendedCountries = getRecommendedCountries(organization.legalCountry);
  const schema = useMemo(
    () =>
      createCompanyActivityFormSchema({
        formatMessage,
      }),
    [formatMessage]
  );

  const { control, handleSubmit, reset } = useForm<
    CompanyActivityFormInputValues,
    unknown,
    CompanyActivityFormValues
  >({
    defaultValues: {
      businessRelationships: organization.businessRelationships,
      businessRelationshipsOther: organization.businessRelationshipsOther,
      incomingTransfersOrigin: organization.incomingTransfersOrigin,
      outgoingTransfersDestination: organization.outgoingTransfersDestination,
    },
    resolver: zodResolver(schema),
    shouldUnregister: true,
  });

  useEffect(() => {
    reset({
      businessRelationships: organization.businessRelationships,
      businessRelationshipsOther: organization.businessRelationshipsOther,
      incomingTransfersOrigin: organization.incomingTransfersOrigin,
      outgoingTransfersDestination: organization.outgoingTransfersDestination,
    });
  }, [
    organization.businessRelationships,
    organization.businessRelationshipsOther,
    organization.incomingTransfersOrigin,
    organization.outgoingTransfersDestination,
    reset,
  ]);

  const businessRelationships = useWatch({ control, name: 'businessRelationships' });
  const shouldShowOther = Boolean(businessRelationships?.includes('other'));

  const incomingLabel = formatMessage({
    id: 'customer-registration-kit.organization-activity.countries-money-in.label',
  });
  const outgoingLabel = formatMessage({
    id: 'customer-registration-kit.organization-activity.countries-money-out.label',
  });

  return (
    <>
      <BackButton
        className={styles.backButton}
        isDisabled={isGoingBack || isSubmitting}
        onPress={goBack}
      />
      <form
        data-test-company-activity
        id="company-activity-form"
        onSubmit={handleSubmit(values => {
          void onSubmit(values);
        })}
      >
        <h1 className="heading-section mb-16" data-test-company-activity-title>
          <FormattedMessage id="customer-registration-kit.organization-activity.business.title" />
        </h1>
        <p className="body-standard mb-32" data-test-company-activity-subtitle>
          <FormattedMessage id="customer-registration-kit.organization-activity.fr.subtitle" />
        </p>

        <Controller
          control={control}
          name="businessRelationships"
          render={({ field, fieldState }) => (
            <>
              <BusinessRelationshipsDropdown
                className={shouldShowOther ? undefined : 'mb-32'}
                errorMessage={
                  fieldState.error?.message ?? formatMessage({ id: 'generic.required-field.error' })
                }
                onSelectionChange={keys => {
                  field.onChange(keys);
                }}
                otherValue=""
                renderOtherField={false}
                selectedKeys={field.value ?? []}
                shouldShowValidationError={fieldState.invalid}
              />
              {shouldShowOther ? (
                <Controller
                  control={control}
                  name="businessRelationshipsOther"
                  render={({ field: otherField, fieldState: otherFieldState }) => (
                    <TextField
                      className="mb-32"
                      aria-label={formatMessage({
                        id: 'customer-registration-kit.business-activity.source-of-funds.options.other-placeholder',
                      })}
                      errorMessage={otherFieldState.error?.message}
                      isInvalid={otherFieldState.invalid}
                      isRequired
                      label=""
                      onBlur={otherField.onBlur}
                      onChange={otherField.onChange}
                      placeholder={formatMessage({
                        id: 'customer-registration-kit.business-activity.source-of-funds.options.other-placeholder',
                      })}
                      value={otherField.value ?? ''}
                    />
                  )}
                />
              ) : null}
            </>
          )}
        />

        <CountryMultiSelectField
          className="mb-32"
          control={control}
          description={formatMessage({
            id: 'customer-registration-kit.business-activity.countries-of-operations-money-in.label',
          })}
          isRequired
          label={incomingLabel}
          name="incomingTransfersOrigin"
          recommendedCountries={recommendedCountries}
        />

        <CountryMultiSelectField
          className="mb-32"
          control={control}
          description={formatMessage({
            id: 'customer-registration-kit.business-activity.countries-of-operations-money-out.label',
          })}
          isRequired
          label={outgoingLabel}
          name="outgoingTransfersDestination"
          recommendedCountries={recommendedCountries}
        />

        <ResponsiveCta
          className="mb-16"
          data-test-company-activity-submit
          isPending={isSubmitting}
          type="submit"
        >
          <FormattedMessage id="generic.continue.cta" />
        </ResponsiveCta>
      </form>
    </>
  );
}
